import type { AppDispatch, GetState } from "@redux/store/reduxStore";
import type { UndoCommand } from "./types";
import { dismissNotification, showNotification } from "@redux/actions/useNotifications";
import { Message } from "@app/_components/Notifications";

function prefixMessage(prefix: string, message: Message): Message {
  if (typeof message === "string") return `${prefix}${message}`;
  return [prefix, ...message];
}

const MAX_STACK_SIZE = 50;

class UndoManager {
  private undoStack: UndoCommand[] = [];
  private redoStack: UndoCommand[] = [];
  private scopeKey = "";
  private _isProcessing = false;

  /** Set to true during undo/redo execution to prevent recording reverse operations */
  isPerformingUndoRedo = false;

  push(command: UndoCommand): void {
    this.undoStack.push(command);
    this.redoStack = [];
    if (this.undoStack.length > MAX_STACK_SIZE) {
      this.undoStack.shift();
    }
  }

  async performUndo(dispatch: AppDispatch, getState: GetState): Promise<void> {
    if (this._isProcessing || !this.undoStack.length) return;
    this._isProcessing = true;
    this.isPerformingUndoRedo = true;
    const command = this.undoStack.pop()!;
    try {
      await command.undo(dispatch, getState);
      this.redoStack.push(command);
      dispatch(dismissNotification(command.notificationId + "_undo"));
      dispatch(
        showNotification({
          id: command.notificationId + "_redo",
          message: prefixMessage("Undid: ", command.description),
          action: { text: "Redo", onClick: () => void this.performRedo(dispatch, getState) },
        })
      );
    } catch (err) {
      console.error(err);
    } finally {
      this.isPerformingUndoRedo = false;
      this._isProcessing = false;
    }
  }

  async performRedo(dispatch: AppDispatch, getState: GetState): Promise<void> {
    if (this._isProcessing || !this.redoStack.length) return;
    this._isProcessing = true;
    this.isPerformingUndoRedo = true;
    const command = this.redoStack.pop()!;
    try {
      await command.redo(dispatch, getState);
      this.undoStack.push(command);
      dispatch(dismissNotification(command.notificationId + "_redo"));
      dispatch(
        showNotification({
          id: command.notificationId + "_undo",
          message: prefixMessage("Redid: ", command.description),
          action: { text: "Undo", onClick: () => void this.performUndo(dispatch, getState) },
        })
      );
    } finally {
      this.isPerformingUndoRedo = false;
      this._isProcessing = false;
    }
  }

  setScopeKey(key: string): void {
    if (key !== this.scopeKey) {
      this.undoStack = [];
      this.redoStack = [];
      this.scopeKey = key;
    }
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }
}

export const undoManager = new UndoManager();
